"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useTransition } from 'react';
import { inventoryPageHref } from '@/utils/inventoryUrls';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';

// Helper function to create pagination URLs
const createPageUrl = inventoryPageHref;
interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isLoading?: boolean;
}
interface PageNumbersProps {
  currentPage: number;
  totalPages: number;
}
const PageNumbers: React.FC<PageNumbersProps> = t0 => {
  const $ = _c(5);
  const {
    currentPage,
    totalPages
  } = t0;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== currentPage || $[1] !== pathname || $[2] !== searchParams || $[3] !== totalPages) {
    const pages = [];
    pages.push(1);
    const start = Math.max(2, currentPage - 2);
    const end = Math.min(totalPages - 1, currentPage + 2);
    if (start > 2) {
      pages.push("...");
    }
    for (let i = start; i <= end; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(i);
      }
    }
    if (end < totalPages - 1) {
      pages.push("...");
    }
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    t1 = <div className="t-flex t-items-center t-space-x-1">{pages.map((page, index) => {
        if (page === "...") {
          return <span key={`ellipsis-${index}`} className="t-px-3 t-py-2 t-text-gray-500 t-text-sm t-font-medium">...</span>;
        }
        const pageNum = page as number;
        const isActive = pageNum === currentPage;
        if (isActive) {
          return <span key={pageNum} className="t-min-w-[44px] t-h-11 t-px-4 t-py-2 t-rounded-lg t-text-sm t-font-semibold t-bg-brand-violet t-text-white t-border t-border-brand-violet t-shadow-md t-flex t-items-center t-justify-center" aria-label={`Current page ${pageNum}`} aria-current="page">{pageNum}</span>;
        }
        return <Link key={pageNum} href={createPageUrl(pathname, searchParams, pageNum)} className="t-min-w-[44px] t-h-11 t-px-4 t-py-2 t-rounded-lg t-text-sm t-font-semibold t-transition-all t-duration-200 t-ease-in-out t-border t-bg-white t-text-gray-700 t-border-gray-300 hover:t-bg-gray-50 hover:t-border-gray-400 hover:t-shadow-sm focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2 t-flex t-items-center t-justify-center t-no-underline" aria-label={`Go to page ${pageNum}`} scroll={true} prefetch={false}>{pageNum}</Link>;
      })}</div>;
    $[0] = currentPage;
    $[1] = pathname;
    $[2] = searchParams;
    $[3] = totalPages;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export const PaginationControls: React.FC<PaginationControlsProps> = t0 => {
  const $ = _c(24);
  const {
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending] = useTransition();
  const isTransitioning = isPending || isLoading;
  if (!Number.isInteger(currentPage) || currentPage < 1 || currentPage > totalPages) {
    return null;
  }
  let t2;
  if ($[0] !== currentPage || $[1] !== hasPreviousPage || $[2] !== isTransitioning || $[3] !== pathname || $[4] !== searchParams) {
    t2 = hasPreviousPage && !isTransitioning ? <Link href={createPageUrl(pathname, searchParams, currentPage - 1)} className="t-flex t-items-center t-px-4 t-py-2 t-rounded-lg t-text-sm t-font-semibold t-transition-all t-duration-200 t-ease-in-out t-border t-bg-white t-text-gray-700 t-border-gray-300 hover:t-bg-gray-50 hover:t-border-gray-400 hover:t-shadow-sm focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2 t-no-underline" aria-label="Go to previous page" scroll={true} prefetch={false} rel="prev"><svg className="t-w-4 t-h-4 t-mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg><span className="t-hidden sm:t-inline">Previous</span><span className="sm:t-hidden">Prev</span></Link> : <span className="t-flex t-items-center t-px-4 t-py-2 t-rounded-lg t-text-sm t-font-semibold t-bg-gray-100 t-text-gray-600 t-border t-border-gray-200 t-cursor-not-allowed" aria-label="Previous page not available"><svg className="t-w-4 t-h-4 t-mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg><span className="t-hidden sm:t-inline">Previous</span><span className="sm:t-hidden">Prev</span></span>;
    $[0] = currentPage;
    $[1] = hasPreviousPage;
    $[2] = isTransitioning;
    $[3] = pathname;
    $[4] = searchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== currentPage || $[7] !== totalPages) {
    t3 = <div className="t-hidden sm:t-flex"><PageNumbers currentPage={currentPage} totalPages={totalPages} /></div>;
    $[6] = currentPage;
    $[7] = totalPages;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== currentPage || $[10] !== hasNextPage || $[11] !== isTransitioning || $[12] !== pathname || $[13] !== searchParams) {
    t4 = hasNextPage && !isTransitioning ? <Link href={createPageUrl(pathname, searchParams, currentPage + 1)} className="t-flex t-items-center t-px-4 t-py-2 t-rounded-lg t-text-sm t-font-semibold t-transition-all t-duration-200 t-ease-in-out t-border t-bg-white t-text-gray-700 t-border-gray-300 hover:t-bg-gray-50 hover:t-border-gray-400 hover:t-shadow-sm focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2 t-no-underline" aria-label="Go to next page" scroll={true} prefetch={false} rel="next"><span className="t-hidden sm:t-inline">Next</span><span className="sm:t-hidden">Next</span><svg className="t-w-4 t-h-4 t-ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></Link> : <span className="t-flex t-items-center t-px-4 t-py-2 t-rounded-lg t-text-sm t-font-semibold t-bg-gray-100 t-text-gray-600 t-border t-border-gray-200 t-cursor-not-allowed" aria-label="Next page not available"><span className="t-hidden sm:t-inline">Next</span><span className="sm:t-hidden">Next</span><svg className="t-w-4 t-h-4 t-ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></span>;
    $[9] = currentPage;
    $[10] = hasNextPage;
    $[11] = isTransitioning;
    $[12] = pathname;
    $[13] = searchParams;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = <div className="t-flex t-items-center t-space-x-2">{t2}{t3}{t4}</div>;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== isTransitioning) {
    t6 = isTransitioning && <div className="t-absolute t-top-full t-left-1/2 t-transform t--translate-x-1/2 t-mt-4 t-flex t-items-center t-text-sm t-text-gray-600"><svg className="t-animate-spin t-w-4 t-h-4 t-mr-2" fill="none" viewBox="0 0 24 24"><circle className="t-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="t-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Loading...</div>;
    $[19] = isTransitioning;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t5 || $[22] !== t6) {
    t7 = <nav className="t-flex t-items-center t-justify-center t-py-8 t-px-4" aria-label="Search results pagination">{t5}{t6}</nav>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
};
export default PaginationControls;